import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Switch,
  TextField,
  Paper,
  Button,
  FormControlLabel,
  Slider,
  Divider,
  Alert,
  Snackbar,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import { 
  PlayArrow, 
  Stop, 
  VolumeUp, 
  Settings, 
  ExpandLess, 
  Mic,
  Save,
  Share,
  Delete,
  Refresh,
  Star,
  StarBorder,
  MusicNote,
  CloudUpload,
  AudioFile,
  InsertDriveFile,
} from '@mui/icons-material';
import { autoJsStoreUtils, autoWebViewJs } from '@droid/android';
import { user } from '@app/model';
import { authService } from '@src/services/authService';
import { ReportButton } from '../report/ReportButton';

// 完整的TTS设置数据结构（整合录制功能后）
interface EmergencyTTSSettings {
  enabled: boolean;
  message: string;
  repeatInterval: number;
  voiceSpeed: number;
  voicePitch: number;
  useCustomAudio: boolean;        // 是否使用自定义录音
  medicalEmergencyAudioPath?: string; // 医疗呼救音频路径
  lastUpdated: number;
}

// TTS文件信息接口（扩展版本）
interface TTSFileInfo {
  fileName: string;
  filePath: string;
  fileSize: number;
  createTime: number;
  type: 'general' | 'medical' | 'security' | 'custom';
  source: 'recorded' | 'uploaded';  // 新增来源标识
  isDefault?: boolean;
  duration?: number;  // 音频时长（毫秒）
  format?: string;    // 文件格式
  mimeType?: string;  // MIME类型
}

// 录制结果接口
interface TTSRecordResult {
  success: boolean;
  filePath: string;
  message: string;
}

// 语音配置接口
interface VoiceEmergencyConfig {
  medicalDefault?: string;
  securityDefault?: string;
  lastUpdated: number;
}

// 文件上传相关接口
interface FileSelectResult {
  success: boolean;
  filePath: string;
  message: string;
  fileInfo?: SelectedFileInfo;
}

interface SelectedFileInfo {
  filePath: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  duration?: number;
}

interface FileUploadResult {
  success: boolean;
  filePath: string;
  message: string;
  fileInfo?: TTSFileInfo;
}

interface FileValidationResult {
  isValid: boolean;
  message: string;
  fileSize: number;
  duration: number;
  format: string;
  warnings: string[];
}

interface SupportedFormats {
  formats: string[];
  maxFileSizeMB: number;
  maxDurationSeconds: number;
  recommendedDurationSeconds: number;
}

// 默认设置
const DEFAULT_SETTINGS: EmergencyTTSSettings = {
  enabled: false,
  message: '我遇到了紧急情况，需要帮助！请立即联系紧急服务！',
  repeatInterval: 3000,
  voiceSpeed: 1.0,
  voicePitch: 1.0,
  useCustomAudio: false,
  lastUpdated: Date.now(),
};

const DEFAULT_VOICE_CONFIG: VoiceEmergencyConfig = {
  lastUpdated: Date.now(),
};

const TTS_SETTINGS_KEY = 'emergencyTTSSettings';
const VOICE_CONFIG_KEY = 'voiceEmergencyConfig';

export const EmergencyTTSSettings: React.FC = () => {
  const [settings, setSettings] = useState<EmergencyTTSSettings>(DEFAULT_SETTINGS);
  const [voiceConfig, setVoiceConfig] = useState<VoiceEmergencyConfig>(DEFAULT_VOICE_CONFIG);
  const [fileList, setFileList] = useState<TTSFileInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  // 录制相关状态
  const [isRecording, setIsRecording] = useState(false);
  const [playingFile, setPlayingFile] = useState<string | null>(null);
  const [recordText, setRecordText] = useState(DEFAULT_SETTINGS.message);
  const [recordSpeed, setRecordSpeed] = useState(1.0);
  const [recordPitch, setRecordPitch] = useState(1.0);
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  const [ttsAvailable, setTtsAvailable] = useState<boolean | null>(null);
  
  // 文件上传相关状态
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<SelectedFileInfo | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [supportedFormats, setSupportedFormats] = useState<SupportedFormats | null>(null);

  async function onRecordClick(){
    if(user.data.storeData.user_token){
      setShowRecordDialog(true)
    }else{
      // 弹出登录页面

      const appLoggedIn = await authService.showLoginDialog();
      if(appLoggedIn){
        setShowRecordDialog(true);
      }
    }
  }
  // 加载所有设置和数据
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // 加载TTS设置
        const savedSettings = await autoJsStoreUtils.read<EmergencyTTSSettings>(
          TTS_SETTINGS_KEY, 
          DEFAULT_SETTINGS
        );
        setSettings(savedSettings);
        
        // 加载语音配置
        const savedConfig = await autoJsStoreUtils.read<VoiceEmergencyConfig>(
          VOICE_CONFIG_KEY,
          DEFAULT_VOICE_CONFIG
        );
        setVoiceConfig(savedConfig);
        
        // 检查TTS可用性
        checkTTSAvailability();
        
        // 加载文件列表
        loadFileList();
      } catch (err) {
        console.error('加载数据失败:', err);
        setError('加载设置失败');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // 初始化时加载支持的音频格式
  useEffect(() => {
    loadSupportedFormats();
  }, []);

  // 加载支持的音频格式
  const loadSupportedFormats = useCallback(() => {
    try {
      const script = `var data = com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.getSupportedAudioFormats();
data;`;
      const { javaResultString } = autoWebViewJs.callScript(script);
      const formats = JSON.parse(javaResultString || '{}') as SupportedFormats;
      setSupportedFormats(formats);
    } catch (error) {
      console.error('加载支持格式失败:', error);
      setSupportedFormats({
        formats: ['mp3', 'wav', 'm4a', 'aac'],
        maxFileSizeMB: 10,
        maxDurationSeconds: 60,
        recommendedDurationSeconds: 30
      });
    }
  }, []);

  // 加载文件列表
  const loadFileList = useCallback(() => {
    try {
      const script = `var data = com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.getFileList();
data;`;
      const { javaResultString: listJson } = autoWebViewJs.callScript(script);
      const files = JSON.parse((listJson || "[]") as string) as TTSFileInfo[];
      
      // 为现有文件设置默认类型（兼容性处理）
      const filesWithType = files.map(file => ({
        ...file,
        type: (file as any).type || 'general' as const,
        isDefault: (file as any).isDefault || false,
      }));
      
      setFileList(filesWithType);
    } catch (error) {
      console.error('加载文件列表失败:', error);
      setFileList([]);
    }
  }, []);

  // 选择并上传音频文件（整合操作）
  const handleSelectAndUploadAudioFile = useCallback(async () => {
    if (isUploading) return;
    
    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      const script = `var data = com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.selectAndUploadAudioFile();
data;`;
      
      // 模拟上传进度
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 300);
      
      const { javaResultString } = autoWebViewJs.callScript(script);
      const result = JSON.parse(javaResultString || '{}') as FileUploadResult;
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (result.success) {
        setSuccessMessage('音频文件上传成功！');
        setShowSuccess(true);
        loadFileList(); // 刷新文件列表
      } else {
        setError(result.message || '上传失败');
      }
    } catch (error) {
      console.error('文件上传失败:', error);
      setError('文件上传异常：' + (error as Error).message);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [isUploading, loadFileList]);

  // 检查TTS可用性
  const checkTTSAvailability = useCallback(() => {
    try {
      const script = `var data = com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.isTTSAvailable();
data;`;
      const { javaResultString } = autoWebViewJs.callScript(script);
      setTtsAvailable(javaResultString === 'true');
    } catch (error) {
      console.error('检查TTS可用性失败:', error);
      setTtsAvailable(false);
    }
  }, []);

  // 保存设置
  const saveSettings = async (newSettings: EmergencyTTSSettings) => {
    try {
      const settingsToSave = {
        ...newSettings,
        lastUpdated: Date.now(),
      };
      
      await autoJsStoreUtils.write(TTS_SETTINGS_KEY, settingsToSave);
      setSettings(settingsToSave);
      setSuccessMessage('设置保存成功');
      setShowSuccess(true);
      setError(null);
    } catch (err) {
      console.error('保存TTS设置失败:', err);
      setError('保存设置失败');
    }
  };

  // 保存语音配置
  const saveVoiceConfig = async (newConfig: VoiceEmergencyConfig) => {
    try {
      const configToSave = {
        ...newConfig,
        lastUpdated: Date.now(),
      };
      
      await autoJsStoreUtils.write(VOICE_CONFIG_KEY, configToSave);
      setVoiceConfig(configToSave);
    } catch (err) {
      console.error('保存语音配置失败:', err);
      setError('保存配置失败');
    }
  };

  // 录制TTS
  const handleRecord = useCallback(() => {
    if (!recordText.trim()) {
      setError('请输入要录制的文字');
      return;
    }

    if (!ttsAvailable) {
      setError('TTS服务不可用，无法录制');
      return;
    }

    try {
      setIsRecording(true);
      // 生成文件名：TTS_YYYYMMDD_HHMMSS.wav
      const now = new Date();
      const timestamp = now.getFullYear().toString() + 
                       (now.getMonth() + 1).toString().padStart(2, '0') + 
                       now.getDate().toString().padStart(2, '0') + '_' +
                       now.getHours().toString().padStart(2, '0') + 
                       now.getMinutes().toString().padStart(2, '0') + 
                       now.getSeconds().toString().padStart(2, '0');
      const fileName = `TTS_${timestamp}.wav`;
      
      const script = `var data = com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.recordTTS("${recordText.replace(/"/g, '\\"')}", "${fileName}", ${recordSpeed}, ${recordPitch});
data;`;
      
      // 模拟异步录制过程
      setTimeout(() => {
        try {
          const { javaResultString: resultJson } = autoWebViewJs.callScript(script);
          console.log('录制结果:', resultJson);
          const result = JSON.parse(resultJson || '{}') as TTSRecordResult;
          
          if (result.success) {
            setSuccessMessage('录制成功！');
            setShowSuccess(true);
            setShowRecordDialog(false);
            loadFileList(); // 刷新文件列表
          } else {
            setError(result.message || '录制失败');
          }
        } catch (error) {
          console.error('录制失败:', error);
          setError('录制异常：' + (error as Error).message);
        } finally {
          setIsRecording(false);
        }
      }, 2000);
    } catch (error) {
      setError('录制异常：' + (error as Error).message);
      setIsRecording(false);
    }
  }, [recordText, recordSpeed, recordPitch, ttsAvailable, loadFileList]);

  // 播放文件
  const handlePlayFile = useCallback((filePath: string) => {
    try {
      if (playingFile === filePath) {
        setPlayingFile(null);
        return;
      }

      const script = `var data = com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.playFile("${filePath.replace(/"/g, '\\"')}");
data;`;
      const { javaResultString: success } = autoWebViewJs.callScript(script);
      if (success === 'true') {
        setPlayingFile(filePath);
        setTimeout(() => setPlayingFile(null), 3000);
      } else {
        setError('播放失败');
      }
    } catch (error) {
      setError('播放异常：' + (error as Error).message);
    }
  }, [playingFile]);

  // 分享文件
  const handleShareFile = useCallback((filePath: string) => {
    try {
      const script = `var data = com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.shareFile("${filePath.replace(/"/g, '\\"')}", "紧急呼救语音");
data;`;
      const { javaResultString: success } = autoWebViewJs.callScript(script);
      if (success !== 'true') {
        setError('分享失败');
      }
    } catch (error) {
      setError('分享异常：' + (error as Error).message);
    }
  }, []);

  // 删除文件
  const handleDeleteFile = useCallback((filePath: string) => {
    if (window.confirm('确定要删除这个文件吗？')) {
      try {
        const script = `var data = com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.deleteFile("${filePath.replace(/"/g, '\\"')}");
data;`;
        const { javaResultString: success } = autoWebViewJs.callScript(script);
        if (success === 'true') {
          setSuccessMessage('文件删除成功');
          setShowSuccess(true);
          loadFileList();
        } else {
          setError('删除失败');
        }
      } catch (error) {
        setError('删除异常：' + (error as Error).message);
      }
    }
  }, [loadFileList]);

  // 设置为默认音频
  const setAsDefaultAudio = useCallback(async (filePath: string, type: 'medical' | 'security') => {
    try {
      // 调用Android端设置默认音频
      const script = `var data = com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.setFileAsDefault("${filePath.replace(/"/g, '\\"')}", "${type}");
data;`;
      const { javaResultString: success } = autoWebViewJs.callScript(script);
      
      if (success !== 'true') {
        setError('设置默认音频失败');
        return;
      }
      
      const newConfig = { ...voiceConfig };
      if (type === 'medical') {
        newConfig.medicalDefault = filePath;
        // 同时更新TTS设置中的路径
        const updatedSettings = {
          ...settings,
          medicalEmergencyAudioPath: filePath,
          useCustomAudio: true,
        };
        setSettings(updatedSettings);
        await saveSettings(updatedSettings);
      } else {
        newConfig.securityDefault = filePath;
      }
      
      await saveVoiceConfig(newConfig);
      
      // 更新文件列表中的默认标记
      const updatedFiles = fileList.map(file => ({
        ...file,
        isDefault: file.filePath === filePath,
      }));
      setFileList(updatedFiles);
      
      setSuccessMessage(`已设置为${type === 'medical' ? '医疗' : '安全'}呼救默认音频`);
      setShowSuccess(true);
    } catch (err) {
      console.error('设置默认音频失败:', err);
      setError('设置默认音频失败');
    }
  }, [voiceConfig, fileList, settings, saveSettings, saveVoiceConfig]);

  // 格式化文件大小
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // 格式化时间
  const formatTime = (timestamp: number): string => {
    return new Date(timestamp).toLocaleString();
  };

  // 格式化时长
  const formatDuration = (milliseconds: number): string => {
    if (!milliseconds || milliseconds <= 0) return '';
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${seconds}s`;
  };

  // 测试TTS
  const testTTS = async () => {
    if (!settings.message.trim()) {
      setError('请输入呼救文字后再测试');
      return;
    }

    try {
      setTesting(true);
      
      // 调用Android端TTS测试方法
      const script = `
        com.fanfanlo.emergencycall.manager.EmergencyTTSManager.testTTS("${settings.message}", ${settings.voiceSpeed}, ${settings.voicePitch});
      `;
      
      await new Promise(resolve => setTimeout(resolve, 500)); // 模拟测试时间
      // TODO: 实际调用时取消注释
      autoWebViewJs.callScript(script);
      
    } catch (err) {
      console.error('TTS测试失败:', err);
      setError('TTS测试失败');
    } finally {
      setTesting(false);
    }
  };

  // 停止TTS测试
  const stopTest = async () => {
    try {
      const script = `
        com.fanfanlo.emergencycall.manager.EmergencyTTSManager.stopTest();
      `;
      // TODO: 实际调用时取消注释
      autoWebViewJs.callScript(script);
      setTesting(false);
    } catch (err) {
      console.error('停止TTS测试失败:', err);
    }
  };

  const handleSettingChange = (key: keyof EmergencyTTSSettings, value: any) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    
    // 对于启用/禁用这样的关键设置，立即保存
    if (key === 'enabled') {
      saveSettings(newSettings);
    }
  };

  const handleSave = () => {
    if (settings.enabled && !settings.useCustomAudio && !settings.message.trim()) {
      setError('启用TTS功能时，呼救文字不能为空');
      return;
    }
    saveSettings(settings);
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography>加载中...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      {/* 折叠头部 */}
      <Paper 
        elevation={1} 
        sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          borderRadius: 1,
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 1, sm: 2 },
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          flex: 1,
          minWidth: 0
        }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontSize: { xs: '0.95rem', sm: '1.25rem' },
              minWidth: 0,
              flexShrink: 0
            }}
          >
            <VolumeUp fontSize="small" />
            <Box component="span" sx={{ 
              display: { xs: 'none', sm: 'inline' }
            }}>紧急呼救语音设置</Box>
            <Box component="span" sx={{ 
              display: { xs: 'inline', sm: 'none' }
            }}>呼救语音</Box>
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 1, sm: 2 },
            flexWrap: { xs: 'wrap', sm: 'nowrap' }
          }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enabled}
                  onChange={(e) => handleSettingChange('enabled', e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={settings.enabled ? '已启用' : '未启用'}
              sx={{ 
                margin: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: { xs: '0.8rem', sm: '0.875rem' }
                }
              }}
            />
            
            {/* 状态指示器 */}
            {/* <Chip 
              size="small"
              icon={ttsAvailable ? <VolumeUp /> : <MusicNote />}
              label={ttsAvailable === null ? '检测中...' : (ttsAvailable ? 'TTS可用' : 'TTS不可用')}
              color={ttsAvailable === null ? 'default' : (ttsAvailable ? 'success' : 'warning')}
              variant="outlined"
              sx={{ 
                fontSize: { xs: '0.7rem', sm: '0.8125rem' },
                height: { xs: '24px', sm: '32px' }
              }}
            /> */}
          </Box>
        </Box>
        
        <Button
          variant="outlined"
          size="small"
          startIcon={expanded ? <ExpandLess /> : <Settings />}
          onClick={() => setExpanded(!expanded)}
          sx={{ 
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            padding: { xs: '4px 8px', sm: '6px 16px' },
            minWidth: { xs: 'auto', sm: '64px' },
            flexShrink: 0
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {expanded ? '收起设置' : '设置'}
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
            {expanded ? '收起' : '设置'}
          </Box>
        </Button>
      </Paper>

      {/* 折叠内容 */}
      <Collapse in={expanded}>
        <Paper 
          elevation={0} 
          sx={{ 
            mt: 1, 
            p: 0,
            border: '1px solid', 
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          {/* 功能标签页 */}
          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
          >
            {/* <Tab label="文字设置" /> */}
            <Tab label="上传/文字转语音" />
            <Tab label="文件管理" />
          </Tabs>

          {/* 标签页内容 */}
          <Box sx={{ p: 2 }}>
            {/* 文字设置 Tab */}
            {activeTab === -1 && (
              <Box>
                {/* 呼救文字设置 */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                    呼救文字内容
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={settings.message}
                    onChange={(e) => handleSettingChange('message', e.target.value)}
                    placeholder="请输入紧急情况下要播报的文字..."
                    disabled={!settings.enabled}
                    helperText={`当前字数: ${settings.message.length} 字符`}
                    sx={{ 
                      '& .MuiInputBase-input': {
                        fontSize: '16px',
                        lineHeight: 1.5,
                      }
                    }}
                  />
                </Box>

                {/* 语音设置 */}
                {settings.enabled && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                      语音参数设置
                    </Typography>

                    {/* 重复间隔 */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        重复间隔: {settings.repeatInterval / 1000} 秒
                      </Typography>
                      <Slider
                        value={settings.repeatInterval}
                        onChange={(_, value) => handleSettingChange('repeatInterval', value)}
                        min={2000}
                        max={10000}
                        step={500}
                        marks={[
                          { value: 2000, label: '2s' },
                          { value: 5000, label: '5s' },
                          { value: 10000, label: '10s' },
                        ]}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value / 1000}s`}
                      />
                    </Box>

                    {/* 语音速度 */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        语音速度: {settings.voiceSpeed}x
                      </Typography>
                      <Slider
                        value={settings.voiceSpeed}
                        onChange={(_, value) => handleSettingChange('voiceSpeed', value)}
                        min={0.5}
                        max={2.0}
                        step={0.1}
                        marks={[
                          { value: 0.5, label: '0.5x' },
                          { value: 1.0, label: '1.0x' },
                          { value: 2.0, label: '2.0x' },
                        ]}
                        valueLabelDisplay="auto"
                      />
                    </Box>

                    {/* 语音音调 */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        语音音调: {settings.voicePitch}x
                      </Typography>
                      <Slider
                        value={settings.voicePitch}
                        onChange={(_, value) => handleSettingChange('voicePitch', value)}
                        min={0.5}
                        max={2.0}
                        step={0.1}
                        marks={[
                          { value: 0.5, label: '低' },
                          { value: 1.0, label: '标准' },
                          { value: 2.0, label: '高' },
                        ]}
                        valueLabelDisplay="auto"
                      />
                    </Box>
                  </>
                )}
              </Box>
            )}

            {/* 录制音频 Tab */}
            {activeTab === 0 && (
              <Box>
                {/* 录制语音区域 */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                    录制新的呼救语音
                  </Typography>
                  
                  {!ttsAvailable ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      TTS服务不可用，无法录制语音。请检查设备设置或联系技术支持。
                    </Alert>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<Mic />}
                      onClick={onRecordClick}
                      disabled={isUploading}
                      sx={{ mb: 3 }}
                    >
                      录制新语音
                    </Button>
                  )}
                  
                  <Typography variant="body2" color="text.secondary">
                    • 点击"录制新语音"开始录制<br />
                    • 可以调节语速和音调参数<br />
                    • 录制完成后会自动保存到文件列表<br />
                    • 在文件管理中可以设置为默认呼救音频
                  </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* 上传音频文件区域 */}
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                    上传音频文件
                  </Typography>
                  
                  <Button
                    variant="outlined"
                    startIcon={isUploading ? <CircularProgress size={16} /> : <CloudUpload />}
                    onClick={handleSelectAndUploadAudioFile}
                    disabled={isUploading}
                    sx={{ mb: 2 }}
                  >
                    {isUploading ? '上传中...' : '选择并上传音频文件'}
                  </Button>
                  
                  {/* 上传进度 */}
                  {isUploading && uploadProgress > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={uploadProgress} 
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        上传中... {uploadProgress}%
                      </Typography>
                    </Box>
                  )}
                  
                  {supportedFormats && supportedFormats.formats && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      支持格式: {supportedFormats.formats.join(', ').toUpperCase()}<br />
                      文件大小限制: {supportedFormats.maxFileSizeMB}MB<br />
                      推荐时长: {supportedFormats.recommendedDurationSeconds}秒内
                    </Typography>
                  )}
                  
                  <Typography variant="body2" color="text.secondary">
                    • 支持从设备中选择现有的音频文件<br />
                    {/* • 文件会被自动复制到应用私有目录<br />
                    • 上传前会自动验证文件格式和大小<br /> */}
                    • 上传成功后可在文件管理中设置为默认音频
                  </Typography>
                </Box>
              </Box>
            )}

            {/* 文件管理 Tab */}
            {activeTab === 1 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    录制文件列表
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Refresh />}
                    onClick={loadFileList}
                  >
                    刷新
                  </Button>
                </Box>

                {fileList.length === 0 ? (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                    暂无录制文件
                  </Typography>
                ) : (
                  <List dense>
                    {fileList.map((file, index) => (
                      <React.Fragment key={file.filePath}>
                        <ListItem sx={{ pl: 0 }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {file.source === 'uploaded' ? <InsertDriveFile fontSize="small" /> : <Mic fontSize="small" />}
                                  <Typography variant="body2" fontWeight="medium">
                                    {file.fileName}
                                  </Typography>
                                </Box>
                                
                                {/* 标签集合 */}
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  {file.isDefault && (
                                    <Chip size="small" label="默认" color="primary" />
                                  )}
                                  <Chip 
                                    size="small" 
                                    label={file.source === 'uploaded' ? '上传' : '录制'} 
                                    color={file.source === 'uploaded' ? 'secondary' : 'default'}
                                    variant="outlined"
                                  />
                                  {file.format && (
                                    <Chip 
                                      size="small" 
                                      label={file.format.toUpperCase()} 
                                      color="default" 
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {formatFileSize(file.fileSize)} • {formatTime(file.createTime)}
                                  {file.duration && file.duration > 0 && ` • ${formatDuration(file.duration)}`}
                                </Typography>
                                {file.mimeType && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {file.mimeType}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => handlePlayFile(file.filePath)}
                                color={playingFile === file.filePath ? "primary" : "default"}
                              >
                                {playingFile === file.filePath ? <Stop /> : <PlayArrow />}
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => setAsDefaultAudio(file.filePath, 'medical')}
                                color={file.isDefault ? "primary" : "default"}
                              >
                                {file.isDefault ? <Star /> : <StarBorder />}
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleShareFile(file.filePath)}
                              >
                                <Share />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteFile(file.filePath)}
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            </Box>
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < fileList.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            )}

            {/* 通用操作按钮 */}
            <Divider sx={{ my: 3 }} />
            
            {/* <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{ minWidth: 100 }}
              >
                保存设置
              </Button>

              {settings.enabled && settings.message.trim() && (
                <>
                  {!testing ? (
                    <Button
                      variant="outlined"
                      startIcon={<PlayArrow />}
                      onClick={testTTS}
                      sx={{ minWidth: 100 }}
                    >
                      测试呼救
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Stop />}
                      onClick={stopTest}
                      sx={{ minWidth: 100 }}
                    >
                      停止测试
                    </Button>
                  )}
                </>
              )}
            </Box> */}

            {/* 使用说明 */}
            <Box sx={{ p: 2, backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>智能音频回退说明:</strong><br />
                • 第1级: 检查是否启用呼救功能<br />
                • 第2级: 优先播放设置为默认的自定义音频文件<br />
                {/* • 第3级: 降级到文字转语音播报<br /> */}
                • 第3级: 最终降级到系统默认提示音<br />
                <br />
                <strong>使用建议:</strong><br />
                • 建议录制或上传个人化的呼救信息，包含您的姓名、病情和紧急联系人<br />
                • 在文件管理中点击五角星设置为默认音频<br />
                • 先测试语音效果，确保在紧急情况下能清楚传达<br />
                • 定期检查和更新呼救音频内容
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Collapse>

      {/* 录制对话框 */}
      <Dialog open={showRecordDialog} onClose={() => setShowRecordDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>录制新语音</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="录制文字内容"
            value={recordText}
            onChange={(e) => setRecordText(e.target.value)}
            margin="normal"
            placeholder="请输入要录制的紧急呼救内容..."
            helperText={`字数: ${recordText.length} 字符`}
          />
          
          {isRecording && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                正在录制中，请稍候...
              </Typography>
            </Box>
          )}
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              录制参数
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                语音速度: {recordSpeed}x
              </Typography>
              <Slider
                value={recordSpeed}
                onChange={(_, value) => setRecordSpeed(value as number)}
                min={0.5}
                max={2.0}
                step={0.1}
                marks={[
                  { value: 0.5, label: '慢' },
                  { value: 1.0, label: '标准' },
                  { value: 2.0, label: '快' },
                ]}
                disabled={isRecording}
              />
            </Box>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                语音音调: {recordPitch}x
              </Typography>
              <Slider
                value={recordPitch}
                onChange={(_, value) => setRecordPitch(value as number)}
                min={0.5}
                max={2.0}
                step={0.1}
                marks={[
                  { value: 0.5, label: '低' },
                  { value: 1.0, label: '标准' },
                  { value: 2.0, label: '高' },
                ]}
                disabled={isRecording}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <ReportButton />
          <Button 
            onClick={() => setShowRecordDialog(false)}
            disabled={isRecording}
          >
            取消
          </Button>
          <Button 
            onClick={handleRecord} 
            variant="contained"
            disabled={!recordText.trim() || isRecording}
            startIcon={isRecording ? undefined : <Mic />}
          >
            {isRecording ? '录制中...' : '开始录制'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 成功提示 */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success">
          {successMessage || 'TTS设置保存成功！'}
        </Alert>
      </Snackbar>

      {/* 错误提示 */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};